// Product changelog, newest release first. The PR check, the What's-new view,
// and the unseen-changes dot all rely on the top entry being the latest, and on
// versions being ordered newest-first.
//
// Copy is CLIENT-FACING: plain language, no codebase or internal terms. Say
// "container", not "CTU"; "document", not "transaction". Describe what the user
// can now do, not how it works.
//
// `version` is the client-facing release number and the current version of
// record: the top entry's version is what the app shows. Bump it only for a
// client-relevant release (major = big/breaking, minor = feature, patch = fix);
// internal-only deploys ship under the current version with no new entry. The
// non-blocking PR check (build/pr-changelog-check.yml) warns when a user-facing
// PR adds no entry or does not move the version forward.

export type ChangeKind = 'new' | 'improved' | 'fixed';

export interface ChangeItem {
  kind: ChangeKind;
  text: string;
}

export interface Release {
  version: string; // client-facing semver, e.g. 2.1.0
  date: string; // ISO date (YYYY-MM-DD) the release shipped
  title: string;
  summary: string;
  changes: ChangeItem[];
}

export const changelog: Release[] = [
  {
    version: '1.8.0',
    date: '2026-10-28',
    title: 'Feasibility Check and Final Check',
    summary:
      'Run Validate’s Feasibility Check to see whether goods can travel together, then its Final Check with packaging.',
    changes: [
      {
        kind: 'new',
        text: 'Run the Feasibility Check as soon as every cargo item has a substance, without clearing every error first.',
      },
      {
        kind: 'new',
        text: 'The Final Check runs Validate’s full check, packaging included, once all errors are cleared, so packaging problems show up before you export.',
      },
      {
        kind: 'improved',
        text: 'The Submit step is now one card with a tab each for the two checks and the export, so running the Final Check no longer replaces the Feasibility Check. A tab says when its result is out of date. Results are no longer kept after you reload the document.',
      },
      {
        kind: 'new',
        text: 'Check results show Restriction and Exemption chips, as Validate does, when one of your team’s custom rules caused or softened a message. Click a chip to see the rule and why it applied.',
      },
      {
        kind: 'fixed',
        text: 'Messages your team has exempted now show at the exempted severity, so they no longer count as violations.',
      },
      {
        kind: 'improved',
        text: 'Check results now look and count like Hazcheck Validate’s: a summary, then a table of your containers. Click a container to see its cargo, clashes, stowage and signage.',
      },
      {
        kind: 'improved',
        text: 'Segregation clashes, which showed as blank lines, now say how the goods must be kept apart, for example "Separated from", with Validate’s reasons. Clashes between containers have their own table.',
      },
      {
        kind: 'improved',
        text: 'The Validation Completed webhook moves to version 2, with check results laid out as Validate sends them. In webhook settings, each example payload now has a version picker and says what changed.',
      },
      {
        kind: 'fixed',
        text: 'Webhook settings now apply to the next webhook sent. Before, a new address, sign-in or signing secret could take up to 15 minutes to be used.',
      },
      {
        kind: 'improved',
        text: 'If your account has 49 CFR, it is now ticked when you upload a DCM, even when your team’s Validate default is IMDG only. Untick it to check a DCM against IMDG alone.',
      },
      {
        kind: 'improved',
        text: 'If your account has 49 CFR, you can now choose it when you upload a DGD. It starts unticked, even when your team’s Validate default includes it.',
      },
    ],
  },
  {
    version: '1.7.0',
    date: '2026-10-20',
    title: 'IMDG amendments and 49 CFR',
    summary:
      'Documents are checked against the same regulations as Hazcheck Validate, and DCMs can add 49 CFR.',
    changes: [
      {
        kind: 'new',
        text: "New documents are checked against your team's default regulations in Hazcheck Validate, including its IMDG amendment, so results match Validate. The upload screen shows which regulations will be used.",
      },
      {
        kind: 'new',
        text: 'If your account has 49 CFR, tick 49 CFR when you upload a DCM to check it alongside IMDG. Whether a technical name is required then follows 49 CFR.',
      },
      {
        kind: 'new',
        text: "When you open Extract from a validation in Hazcheck Validate, the document uses that validation's regulations.",
      },
      {
        kind: 'improved',
        text: "When you add a document to an existing validation, Extract shows if that validation uses different regulations. If it uses a different IMDG amendment, the document can't be added.",
      },
      {
        kind: 'fixed',
        text: 'Technical name now shows as required for N.O.S. cargo declared as a marine pollutant.',
      },
    ],
  },
  {
    version: '1.6.6',
    date: '2026-10-19',
    title: 'Typed-in details on filled-in PDFs',
    summary:
      'Details typed into a PDF form with a PDF editor now appear in previews and are read during extraction.',
    changes: [
      {
        kind: 'fixed',
        text: 'Values typed onto a PDF with a PDF editor, such as port of loading or booking number, no longer go missing from the page preview or the extracted results.',
      },
    ],
  },
  {
    version: '1.6.5',
    date: '2026-10-12',
    title: 'Tidier workflow screen',
    summary: 'Upload and Close in the document menu, and one issue list open at a time.',
    changes: [
      {
        kind: 'improved',
        text: 'Upload another document is now in the More actions menu.',
      },
      {
        kind: 'new',
        text: 'Close document returns you to the upload screen. The document stays in Jobs.',
      },
      {
        kind: 'improved',
        text: 'Opening the errors or warnings list closes the other.',
      },
    ],
  },
  {
    version: '1.6.4',
    date: '2026-10-12',
    title: 'Faster Verify',
    summary: 'Verify now checks only the cargo that still need it.',
    changes: [
      {
        kind: 'improved',
        text: 'Verify checks only cargo that failed or were never checked, for example "Verify 3". Re-check all substances in More actions checks every cargo.',
      },
      {
        kind: 'fixed',
        text: 'Verify no longer turns orange while you type.',
      },
      {
        kind: 'fixed',
        text: 'Checking one cargo no longer loses the results for the rest.',
      },
    ],
  },
  {
    version: '1.6.3',
    date: '2026-10-12',
    title: 'Variant and error fixes',
    summary:
      'Varariant picks save the document straight away, and made some fixes to always show the right errors when you come back to a document.',
    changes: [
      {
        kind: 'fixed',
        text: 'Saving your choices in the variant picker now saves the document too, so the picks are kept if you leave the page. If the save fails, the picker stays open so you can try again.',
      },
      {
        kind: 'fixed',
        text: 'Reopening a document soon after saving it could show errors you had already fixed. It now shows the errors as they were saved.',
      },
      {
        kind: 'fixed',
        text: 'A cargo whose substance check timed out stayed that way until you pressed Verify. Extract now tries again when you open the document.',
      },
    ],
  },
  {
    version: '1.6.2',
    date: '2026-10-01',
    title: 'Webhook settings fix',
    summary: 'Saving your webhook settings keeps the sign-in details you set up.',
    changes: [
      {
        kind: 'fixed',
        text: 'Saving webhook settings no longer switches off a bearer token or OAuth sign-in, or clears your OAuth details.',
      },
    ],
  },
  {
    version: '1.6.1',
    date: '2026-09-30',
    title: 'Updated user guide',
    summary: 'The user guide matches the app again and shows the real screens you use.',
    changes: [
      {
        kind: 'improved',
        text: 'The user guide is up to date. Most of its examples are now the real screens, so you can try them out.',
      },
      {
        kind: 'improved',
        text: 'Webhook addresses must start with https://, so tokens and signed data are never sent unencrypted.',
      },
      {
        kind: 'fixed',
        text: 'A webhook using OAuth now fails with a clear reason when it cannot get a token, instead of sending without one.',
      },
      {
        kind: 'fixed',
        text: 'A batch with a cancelled document in it now shows as finished.',
      },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-09-28',
    title: 'Choose substance variants in one place',
    summary:
      'Cargo that needs a substance variant is grouped, so a manifest takes a handful of choices instead of one per cargo.',
    changes: [
      {
        kind: 'new',
        text: 'Pick variants from the error banner. If several substances need a variant selected, you can do so from one place.',
      },
      {
        kind: 'improved',
        text: 'Variants are compared side by side. Details that differ between them, such as stowage or packing instructions, are shown in bold.',
      },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-09-18',
    title: 'Model improvements',
    summary: 'Extract now reads segregation groups and flashpoints more reliably.',
    changes: [
      {
        kind: 'improved',
        text: 'Segregation groups written in different styles, such as a named or numbered group, are now read correctly.',
      },
      {
        kind: 'improved',
        text: 'Flashpoints are now read more reliably.',
      },
    ],
  },
  {
    version: '1.4.1',
    date: '2026-09-14',
    title: 'Correct packaging suggestions per container',
    summary: 'Packaging suggestions now always reflect the container you are viewing.',
    changes: [
      {
        kind: 'fixed',
        text: 'The suggested packaging match now always matches the container you are viewing, instead of sometimes carrying over the match from another container with the same goods.',
      },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-09-14',
    title: 'A fresh look and clearer messages',
    summary:
      "Extract has a cleaner look, and when it can't read something from a document it displays a clearer message.",
    changes: [
      { kind: 'improved', text: 'A cleaner, refreshed look.' },
      {
        kind: 'improved',
        text: "When a document can't be read, for example it has no cargo on it or a page won't scan, you now get a clear message instead of a silent failure.",
      },
      {
        kind: 'fixed',
        text: "The 'errors only' view now resets when you move to a different document, so you always start by seeing everything.",
      },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-09-10',
    title: 'Placarding for UN Numbers',
    summary: 'Refreshed placarding display',
    changes: [
      { kind: 'new', text: 'Placarding on dangerous goods declarations for UN Numbers.' },
      {
        kind: 'improved',
        text: 'A dangerous goods declaration with no dangerous goods on it now lets you retry or stop, instead of getting stuck.',
      },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-09-08',
    title: 'Settings inside Validate, and easier naming',
    summary:
      'Change your Extract settings without leaving Validate, and let matching container names sort themselves out.',
    changes: [
      { kind: 'new', text: 'Change your Extract settings without leaving Validate.' },
      {
        kind: 'improved',
        text: "When a container has the same name as one already there, it's now kept and renamed for you, instead of asking you to replace it.",
      },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-09-07',
    title: 'Safer saving',
    summary: 'Fewer surprises when saving your work.',
    changes: [
      {
        kind: 'new',
        text: 'Extract now asks you to confirm before an update removes any containers.',
      },
      { kind: 'fixed', text: 'Packing groups extracted when not using Roman numerals.' },
    ],
  },
  {
    version: '1.0.0',
    date: '2026-09-01',
    title: 'Generic packaging fallback',
    summary: 'An option for admins to fall back to generic packaging automatically.',
    changes: [
      {
        kind: 'fixed',
        text: "When a packaging code isn't recognised, Extract now uses a generic packaging type instead of leaving it blank. This can only be enabled by admins",
      },
      { kind: 'improved', text: 'The document view now clears when you go back to home.' },
    ],
  },
];

export const currentVersion = changelog[0]?.version ?? '';
